/**
 * Ballaststoffe, Zucker, Salz und ausgewählte Vitamine/Mineralstoffe
 * (16.10.2026) — gespeichert als `nutrients` (Schlüssel → Menge) an
 * Lebensmitteln (je 100 g), Gerichten (je Portion) und im Protokoll
 * (eingefroren, wie die Makros).
 *
 * Vergleichswerte mit Quelle (docs/fachkonzept.md §2, „Nährstoffe“):
 * Ballaststoffe als DGE-Richtwert (mindestens), Zucker und Salz als
 * Referenzmenge der LMIV (Anhang XIII Teil B — dieselbe Zahl wie die
 * Prozentangabe auf Packungen), Vitamine und Mineralstoffe als
 * Nährstoffbezugswert (NRV, Anhang XIII Teil A). Das sind Bezugsgrößen,
 * kein persönlicher Bedarf und keine Verordnung.
 *
 * **Fehlt ein Wert, ist er unbekannt — nie 0.** Eine Summe, in der Einträge
 * ohne Angabe stecken, ist „mindestens“, nicht vollständig.
 */

export const PACKAGE_NUTRIENTS = ['fiber', 'sugar', 'salt'] as const;

export const MICRONUTRIENTS = [
  'vitaminD',
  'vitaminB12',
  'folate',
  'vitaminC',
  'iron',
  'calcium',
  'magnesium',
  'zinc',
  'iodine',
] as const;

export const NUTRIENTS = [...PACKAGE_NUTRIENTS, ...MICRONUTRIENTS] as const;

export type NutrientKey = (typeof NUTRIENTS)[number];
export type Micronutrient = (typeof MICRONUTRIENTS)[number];

/** Menge je Nährstoff; was fehlt, ist unbekannt. */
export type Nutrients = Partial<Record<NutrientKey, number>>;

export interface NutrientInfo {
  unit: 'g' | 'mg' | 'µg';
  reference: number;
  /** `min`: mindestens erreichen · `max`: Bezugsgröße nach oben · `nrv`: Bezugswert. */
  kind: 'min' | 'max' | 'nrv';
  source: 'dge' | 'lmivReference' | 'lmivNrv';
}

export const NUTRIENT_INFO: Record<NutrientKey, NutrientInfo> = {
  fiber: { unit: 'g', reference: 30, kind: 'min', source: 'dge' },
  sugar: { unit: 'g', reference: 90, kind: 'max', source: 'lmivReference' },
  salt: { unit: 'g', reference: 6, kind: 'max', source: 'lmivReference' },
  vitaminD: { unit: 'µg', reference: 5, kind: 'nrv', source: 'lmivNrv' },
  vitaminB12: { unit: 'µg', reference: 2.5, kind: 'nrv', source: 'lmivNrv' },
  folate: { unit: 'µg', reference: 200, kind: 'nrv', source: 'lmivNrv' },
  vitaminC: { unit: 'mg', reference: 80, kind: 'nrv', source: 'lmivNrv' },
  iron: { unit: 'mg', reference: 14, kind: 'nrv', source: 'lmivNrv' },
  calcium: { unit: 'mg', reference: 800, kind: 'nrv', source: 'lmivNrv' },
  magnesium: { unit: 'mg', reference: 375, kind: 'nrv', source: 'lmivNrv' },
  zinc: { unit: 'mg', reference: 10, kind: 'nrv', source: 'lmivNrv' },
  iodine: { unit: 'µg', reference: 150, kind: 'nrv', source: 'lmivNrv' },
};

export function isNutrientKey(value: unknown): value is NutrientKey {
  return typeof value === 'string' && (NUTRIENTS as readonly string[]).includes(value);
}

/** Nur bekannte Schlüssel mit endlichen, nicht negativen Zahlen. */
export function cleanNutrients(raw: unknown): Nutrients {
  if (!raw || typeof raw !== 'object') return {};
  const clean: Nutrients = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (isNutrientKey(key) && typeof value === 'number' && Number.isFinite(value) && value >= 0) {
      clean[key] = value;
    }
  }
  return clean;
}

export function scaleNutrients(nutrients: Nutrients | null | undefined, factor: number): Nutrients {
  const scaled: Nutrients = {};
  for (const [key, value] of Object.entries(cleanNutrients(nutrients))) {
    scaled[key as NutrientKey] = Math.round(value * factor * 1000) / 1000;
  }
  return scaled;
}

/** Je 100 g → für `grams` Gramm. */
export function nutrientsForAmount(
  per100g: Nutrients | null | undefined,
  grams: number,
): Nutrients {
  return scaleNutrients(per100g, grams / 100);
}

/**
 * Je Portion aus Zutaten — ein Nährstoff zählt **nur, wenn jede Zutat ihn
 * kennt**. Sonst wäre die Summe eine Untergrenze, die wie ein Wert aussieht.
 */
export function nutrientsPerPortion(
  ingredients: readonly { per100g: Nutrients | null | undefined; grams: number }[],
  portions: number,
): Nutrients {
  if (ingredients.length === 0 || portions <= 0) return {};
  const result: Nutrients = {};
  for (const key of NUTRIENTS) {
    let total = 0;
    let complete = true;
    for (const ingredient of ingredients) {
      const value = cleanNutrients(ingredient.per100g)[key];
      if (value === undefined) {
        complete = false;
        break;
      }
      total += (value * ingredient.grams) / 100;
    }
    if (complete) result[key] = Math.round((total / portions) * 1000) / 1000;
  }
  return result;
}

export type NutrientLevel = 'unknown' | 'below' | 'reached' | 'above';

export interface NutrientStatus {
  key: NutrientKey;
  /** Summe der bekannten Werte (je Tag im Schnitt, wenn `days` > 1) — `null` ohne einen. */
  amount: number | null;
  /** Alle Einträge kennen den Wert. Sonst ist `amount` eine Untergrenze. */
  complete: boolean;
  known: number;
  entries: number;
  /** Anteil am Vergleichswert, 0… (kann über 1 liegen). */
  share: number | null;
  level: NutrientLevel;
}

/**
 * Stand eines Nährstoffs über Einträge (ein Tag, oder `days` Tage im Schnitt).
 * „Erreicht“ heißt bei `min`/`nrv` am Vergleichswert, bei `max` darunter;
 * „darüber“ gibt es nur für `max`.
 */
export function nutrientStatus(
  key: NutrientKey,
  entries: readonly (Nutrients | null | undefined)[],
  days = 1,
): NutrientStatus {
  const info = NUTRIENT_INFO[key];
  let total = 0;
  let known = 0;
  for (const entry of entries) {
    const value = cleanNutrients(entry)[key];
    if (value === undefined) continue;
    total += value;
    known += 1;
  }
  // Ein Tag ohne Einträge ist kein unbekannter Tag, sondern einer mit 0 —
  // wie die Makros daneben (18.10.2026: Zucker und Salz fehlten dann ganz).
  if (entries.length === 0) {
    return { key, amount: 0, complete: true, known: 0, entries: 0, share: 0, level: 'below' };
  }
  if (known === 0) {
    return {
      key,
      amount: null,
      complete: false,
      known,
      entries: entries.length,
      share: null,
      level: 'unknown',
    };
  }
  const amount = Math.round((total / Math.max(1, days)) * 100) / 100;
  const share = amount / info.reference;
  const complete = known === entries.length;
  let level: NutrientLevel;
  if (info.kind === 'max') level = share > 1 ? 'above' : complete ? 'reached' : 'unknown';
  else level = share >= 1 ? 'reached' : 'below';
  return { key, amount, complete, known, entries: entries.length, share, level };
}
